// One place for the API paths and the shape of what comes back.
//
// Every read takes the AbortSignal TanStack Query hands to its query function,
// so a request whose page is already gone stops travelling.

import { queryString, request } from './client'
import type {
  CompanyDetail,
  CompanyMovement,
  CompanyRow,
  Health,
  IntermediaryBrief,
  IntermediaryDetail,
  Movement,
  Page,
  Profile,
  Ranking,
  Series,
  SnapshotDetail,
  SnapshotRow,
  Summary,
  Token,
} from '../types'

/** Query keys are the API query parameter names, so they travel unchanged. */
export interface MovementFilters {
  from?: string
  to?: string
  type?: string[]
  company?: string
  section?: string
  province?: string
  legal_form?: string
  registration_number?: string
}

export interface IntermediaryFilters {
  q?: string
  section?: string
  province?: string
  legal_form?: string
}

export interface IngestResult {
  status: string
  date: string
  message: string
}

export const api = {
  login: (email: string, password: string, totp?: string) =>
    request<Token>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, totp: totp || null }),
      skipRefresh: true,
    }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST', skipRefresh: true }),
  me: (signal?: AbortSignal) => request<Profile>('/api/me', { signal }),

  health: (signal?: AbortSignal) => request<Health>('/api/health', { signal }),
  summary: (days = 30, signal?: AbortSignal) =>
    request<Summary>(`/api/stats/summary${queryString({ days })}`, { signal }),
  series: (key: string, from?: string, to?: string, signal?: AbortSignal) =>
    request<Series>(`/api/stats/series${queryString({ key, from, to })}`, { signal }),
  ranking: (days = 30, size = 10, signal?: AbortSignal) =>
    request<Ranking>(`/api/stats/ranking${queryString({ days, size })}`, { signal }),

  movements: (filters: MovementFilters, cursor?: string, limit = 50, signal?: AbortSignal) =>
    request<Page<Movement>>(`/api/movements${queryString({ ...filters, cursor, limit })}`, {
      signal,
    }),

  intermediaries: (filters: IntermediaryFilters, cursor?: string, signal?: AbortSignal) =>
    request<Page<IntermediaryBrief>>(`/api/intermediaries${queryString({ ...filters, cursor })}`, {
      signal,
    }),
  intermediary: (number: string, signal?: AbortSignal) =>
    request<IntermediaryDetail>(`/api/intermediaries/${encodeURIComponent(number)}`, { signal }),
  intermediaryHistory: (number: string, cursor?: string, signal?: AbortSignal) =>
    request<Page<Movement>>(
      `/api/intermediaries/${encodeURIComponent(number)}/history${queryString({ cursor })}`,
      { signal },
    ),

  companies: (q?: string, signal?: AbortSignal) =>
    request<Page<CompanyRow>>(`/api/companies${queryString({ q })}`, { signal }),
  company: (code: string, days = 90, signal?: AbortSignal) =>
    request<CompanyDetail>(`/api/companies/${encodeURIComponent(code)}${queryString({ days })}`, {
      signal,
    }),
  companyMovements: (code: string, from?: string, to?: string, signal?: AbortSignal) =>
    request<Page<CompanyMovement>>(
      `/api/companies/${encodeURIComponent(code)}/movements${queryString({ from, to })}`,
      { signal },
    ),

  snapshots: (signal?: AbortSignal) => request<Page<SnapshotRow>>('/api/snapshots', { signal }),
  snapshot: (date: string, signal?: AbortSignal) =>
    request<SnapshotDetail>(`/api/snapshots/${encodeURIComponent(date)}`, { signal }),
  ingest: (force = false) =>
    request<IngestResult>(`/api/snapshots/ingest${queryString({ force })}`, { method: 'POST' }),

  users: (signal?: AbortSignal) => request<Profile[]>('/api/users', { signal }),
}
